/**
 * variant-fix-featured-product.js  (v2)
 * ---------------------------------------------------------------
 * Corrige la sélection de variante (image + prix + input[name="id"])
 * sur les sections "featured-product" (Dawn) intégrées sur une page
 * collection, quand le mécanisme natif de mise à jour ne se déclenche
 * pas (conflit d'app, section dupliquée, etc.)
 *
 * Pourquoi cette version fonctionne là où la v1 échouait :
 * Le <script type="application/json"> embarqué dans la section ne
 * contient QUE la variante actuellement sélectionnée (pas la liste
 * complète). Impossible donc de "matcher" une variante côté client
 * à partir de ce JSON. Cette version interroge à la place l'endpoint
 * natif Shopify /products/{handle}.js, qui renvoie TOUJOURS la liste
 * complète des variantes (id, options, prix, image), indépendamment
 * du thème ou des apps installées.
 * ---------------------------------------------------------------
 */
(function () {
  "use strict";

  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }

  var productCache = {}; // handle -> Promise<product>

  function fetchProduct(handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetch("/products/" + handle + ".js").then(function (r) {
        return r.json();
      });
    }
    return productCache[handle];
  }

  // Récupère le handle produit depuis <product-info data-url="/products/xxx">
  function getProductHandle(section) {
    var pi = section.querySelector("product-info[data-url]");
    if (!pi) return null;
    var url = pi.getAttribute("data-url");
    var match = url.match(/\/products\/([^/?]+)/);
    return match ? match[1] : null;
  }

  // Regroupe les valeurs sélectionnées, une par fieldset (= une par option), dans l'ordre du DOM
  function getSelectedOptionValues(section) {
    var fieldsets = section.querySelectorAll("fieldset");
    var values = [];
    fieldsets.forEach(function (fs) {
      var checked = fs.querySelector("input[type='radio']:checked");
      if (checked) values.push(checked.value);
    });
    return values;
  }

  function findMatchingVariant(product, selectedValues) {
    if (!selectedValues.length) return null;
    return (
      product.variants.find(function (variant) {
        var opts = [variant.option1, variant.option2, variant.option3].filter(Boolean);
        if (opts.length !== selectedValues.length) return false;
        return opts.every(function (val, idx) {
          return val === selectedValues[idx];
        });
      }) || null
    );
  }

  function resizeImageUrl(url, width) {
    if (!url) return url;
    return url.replace(/(\.[a-zA-Z0-9]+)(\?.*)?$/, function (match, ext, query) {
      return "_" + width + "x" + ext + (query || "");
    });
  }

  function updateVariantImage(section, product, variant) {
    // Si la variante n'a pas sa propre image, on retombe sur l'image du produit
    var media = variant.featured_image || product.featured_image;
    if (!media) return;

    var src = typeof media === "string" ? media : media.src;
    if (!src) return;

    var img = section.querySelector(
      ".product__media img, .featured-product img, [data-media-id] img, .product-media-container img"
    );
    if (img) {
      img.src = resizeImageUrl(src, img.naturalWidth || img.width || 800);
      img.srcset = "";
    }
  }

  function updatePrice(section, variant) {
    var priceWrapper = section.querySelector(".price");
    if (!priceWrapper) return;

    var regular = priceWrapper.querySelector(".price-item--regular");
    var sale = priceWrapper.querySelector(".price-item--sale");
    var formatted = formatMoney(variant.price);
    var compareFormatted = variant.compare_at_price ? formatMoney(variant.compare_at_price) : null;

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      priceWrapper.classList.add("price--on-sale");
      if (sale) sale.textContent = formatted;
      var compareEl = priceWrapper.querySelector(
        ".price-item--regular.price__compare, s.price-item--regular, .compare-at-price"
      );
      if (compareEl) compareEl.textContent = compareFormatted;
    } else if (regular) {
      regular.textContent = formatted;
    }
  }

  function formatMoney(cents) {
    // Format simple en euros ; ajuste si besoin selon la devise de la boutique
    return (cents / 100).toFixed(2).replace(".", ",") + " €";
  }

  function updateHiddenVariantId(section, variant) {
    var input = section.querySelector("input[name='id']");
    if (!input) return;
    input.value = variant.id;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  function handleVariantChange(section, handle) {
    fetchProduct(handle).then(function (product) {
      var selectedValues = getSelectedOptionValues(section);
      var variant = findMatchingVariant(product, selectedValues);
      if (!variant) return; // combinaison invalide/épuisée, on ne casse rien

      updateHiddenVariantId(section, variant);
      updateVariantImage(section, product, variant);
      updatePrice(section, variant);

      section.dispatchEvent(
        new CustomEvent("variant-fix:changed", { detail: { variant: variant }, bubbles: true })
      );
    });
  }

  function initSection(section) {
    var handle = getProductHandle(section);
    if (!handle) return;

    // Délégation d'événements : robuste même si une app recrée les inputs
    ["click", "change"].forEach(function (evt) {
      section.addEventListener(evt, function (e) {
        if (e.target && e.target.matches("input[type='radio']")) {
          handleVariantChange(section, handle);
        }
      });
    });
  }

  function initAll() {
    var sections = document.querySelectorAll(
      "[data-section-type='featured-product'], [data-section-type*='featured_product'], .shopify-section[id*='featured_product'], .shopify-section[id*='featured-product'], section[data-type='featured-product']"
    );
    sections.forEach(initSection);
  }

  ready(initAll);

  document.addEventListener("shopify:section:load", function (e) {
    initAll();
  });
})();